import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { Copy, Power, Rocket } from 'lucide-react';
import { api } from '../../utils/apiClient';
import { showToast } from '../../utils/toast';

/**
 * CFG-02 Installation — Publish / Disable storefront widget (soft gate via widgetPublished).
 */
const InstallationPage = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [status, setStatus] = useState(null);
  const [loaderUrl, setLoaderUrl] = useState('');
  const [scriptSnippet, setScriptSnippet] = useState('');
  const [message, setMessage] = useState('');
  const [disableOpen, setDisableOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getWidgetStatus();
      setStatus(data?.status || null);
      setLoaderUrl(data?.loaderUrl || '');
      setScriptSnippet(data?.scriptSnippet || '');
      setMessage(data?.message || '');
    } catch (err) {
      showToast(err.message || t('settings.installation.toast.loadFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const publish = async () => {
    setActing(true);
    try {
      const data = await api.publishWidget();
      setStatus(data?.status || null);
      if (data?.loaderUrl) setLoaderUrl(data.loaderUrl);
      if (data?.scriptSnippet) setScriptSnippet(data.scriptSnippet);
      showToast(t('settings.installation.toast.published'), 'success');
    } catch (err) {
      showToast(err.message || t('settings.installation.toast.publishFailed'), 'error');
    } finally {
      setActing(false);
    }
  };

  const disable = async () => {
    setActing(true);
    try {
      const data = await api.disableWidget();
      setStatus(data?.status || null);
      setDisableOpen(false);
      showToast(t('settings.installation.toast.disabled'), 'success');
    } catch (err) {
      showToast(err.message || t('settings.installation.toast.disableFailed'), 'error');
    } finally {
      setActing(false);
    }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast(t('settings.installation.toast.copied', { label }), 'success');
    } catch {
      showToast(t('settings.installation.toast.copyFailed'), 'error');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  const live = Boolean(status?.live);

  return (
    <div className="py-3 px-2">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <h2 className="h4 mb-0">{t('settings.installation.title')}</h2>
            {live ? (
              <Badge bg="success" pill>
                {t('settings.installation.liveBadge')}
              </Badge>
            ) : (
              <Badge bg="secondary" pill>
                {t('settings.installation.notPublished')}
              </Badge>
            )}
          </div>
          <p className="text-muted mb-0">
            {t('settings.installation.subtitle')}
          </p>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <Button
            variant="primary"
            className="d-flex align-items-center gap-1"
            onClick={publish}
            disabled={acting || live}
          >
            <Rocket size={16} />
            {acting && !disableOpen ? t('settings.installation.publishing') : t('settings.installation.publishWidget')}
          </Button>
          <Button
            variant="outline-danger"
            className="d-flex align-items-center gap-1"
            onClick={() => setDisableOpen(true)}
            disabled={acting || !live}
          >
            <Power size={16} />
            {t('settings.installation.disableWidget')}
          </Button>
        </div>
      </div>

      {!live && (
        <Alert variant="warning" className="small">
          {t('settings.installation.warnBefore')} <strong>{t('settings.installation.warnBoldPublish')}</strong> {t('settings.installation.warnAfter')} <code>customJsUrl</code> {t('settings.installation.warnAfterCode')}
        </Alert>
      )}

      {live && (
        <Alert variant="success" className="small">
          {t('settings.installation.publishedPrefix')}
          {status?.widgetPublishedAt
            ? t('settings.installation.publishedSince', { date: new Date(status.widgetPublishedAt).toLocaleString(i18n.language) })
            : ''}
          {t('settings.installation.quotesToggle', { state: status?.enableQuotes ? t('settings.installation.on') : t('settings.installation.off') })}
        </Alert>
      )}

      <div className="bg-white border rounded p-3 shadow-sm mb-3">
        <h3 className="h6 mb-2">{t('settings.installation.loaderTitle')}</h3>
        <p className="text-muted small">{message}</p>
        <Form.Control as="textarea" rows={2} readOnly value={loaderUrl} className="font-monospace small mb-2" />
        <Button
          size="sm"
          variant="outline-primary"
          className="d-flex align-items-center gap-1"
          onClick={() => copy(loaderUrl, t('settings.installation.labelLoaderUrl'))}
        >
          <Copy size={14} />
          {t('settings.installation.copyLoaderUrl')}
        </Button>
      </div>

      <div className="bg-white border rounded p-3 shadow-sm">
        <h3 className="h6 mb-2">{t('settings.installation.snippetTitle')}</h3>
        <Form.Control as="textarea" rows={6} readOnly value={scriptSnippet} className="font-monospace small mb-2" />
        <Button
          size="sm"
          variant="outline-secondary"
          className="d-flex align-items-center gap-1"
          onClick={() => copy(scriptSnippet, t('settings.installation.labelSnippet'))}
        >
          <Copy size={14} />
          {t('settings.installation.copySnippet')}
        </Button>
      </div>

      <Modal show={disableOpen} onHide={() => !acting && setDisableOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('settings.installation.disableModalTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('settings.installation.disableModalBody')}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setDisableOpen(false)} disabled={acting}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={disable} disabled={acting}>
            {acting ? t('settings.installation.disabling') : t('settings.installation.disableWidget')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default InstallationPage;
